/**
 * @fileoverview Cache Utility - Provides caching functionality for API responses
 * @description This utility implements in-memory caching with TTL support for API responses
 * @version 1.0.0
 * @author Fynverse Team
 * @created 2024-01-01
 * @updated 2024-01-01
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheOptions {
  ttl?: number; // Default TTL in milliseconds (default: 5 minutes)
  maxSize?: number; // Maximum cache size (default: 100)
}

/**
 * @class CacheManager
 * @description In-memory cache manager with TTL support
 */
class CacheManager {
  private cache = new Map<string, CacheEntry<any>>();
  private defaultTTL: number;
  private maxSize: number;

  constructor(options: CacheOptions = {}) {
    this.defaultTTL = options.ttl || 5 * 60 * 1000; // 5 minutes
    this.maxSize = options.maxSize || 100;
  }

  /**
   * @method set
   * @description Store data in cache
   * @param key - Cache key
   * @param data - Data to cache
   * @param ttl - Optional TTL override
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.maxSize) {
      this.evictOldest();
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL
    });
  }

  /**
   * @method get
   * @description Retrieve data from cache
   * @param key - Cache key
   * @returns Cached data or null if not found/expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if entry has expired
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * @method has
   * @description Check if key exists in cache and is not expired
   * @param key - Cache key
   * @returns True if key exists and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * @method delete
   * @description Remove entry from cache
   * @param key - Cache key
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * @method deleteByPrefix
   * @description Remove all entries whose keys start with the given prefix
   * @param prefix - Key prefix to match
   */
  deleteByPrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * @method clear
   * @description Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * @method size
   * @description Get current cache size
   * @returns Number of entries in cache
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * @method generateKey
   * @description Generate cache key from parameters
   * @param baseKey - Base key
   * @param params - Parameters to include in key
   * @returns Generated cache key
   */
  generateKey(baseKey: string, params?: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
      return baseKey;
    }

    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {} as Record<string, any>);

    return `${baseKey}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * @method evictOldest
   * @description Remove oldest cache entry
   * @private
   */
  private evictOldest(): void {
    let oldestKey = '';
    let oldestTimestamp = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * @method cleanup
   * @description Remove expired entries from cache
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * @method getStats
   * @description Get cache statistics
   * @returns Cache statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    hitRate: number;
    entries: Array<{ key: string; age: number; ttl: number }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: now - entry.timestamp,
      ttl: entry.ttl
    }));

    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitRate: 0, // Would need to track hits/misses to calculate
      entries
    };
  }
}

// Create default cache instance
export const defaultCache = new CacheManager({
  ttl: 5 * 60 * 1000, // 5 minutes
  maxSize: 100
});

// Create specialized cache instances
export const apiCache = new CacheManager({
  ttl: 2 * 60 * 1000, // 2 minutes for API responses
  maxSize: 50
});

export const userCache = new CacheManager({
  ttl: 10 * 60 * 1000, // 10 minutes for user data
  maxSize: 20
});

export const configCache = new CacheManager({
  ttl: 30 * 60 * 1000, // 30 minutes for configuration data
  maxSize: 10
});

// Export the class for custom instances
export { CacheManager };

// Utility function to create cache key for API calls
export const createApiCacheKey = (endpoint: string, params?: Record<string, any>): string => {
  return apiCache.generateKey(endpoint, params);
};

// Utility function to cache API response
export const cacheApiResponse = <T>(
  key: string,
  data: T,
  ttl?: number
): T => {
  apiCache.set(key, data, ttl);
  return data;
};

// Utility function to get cached API response
export const getCachedApiResponse = <T>(key: string): T | null => {
  return apiCache.get<T>(key);
};

// Utility function to clear cached API response
export const clearCachedApiResponse = (key: string): void => {
  apiCache.delete(key);
};

// Utility function to clear cached API responses by prefix (e.g. dashboard/overview, dashboard/gst)
export const clearCachedApiResponseByPrefix = (prefix: string): void => {
  apiCache.deleteByPrefix(prefix);
};

// Cleanup expired entries every 5 minutes
setInterval(() => {
  apiCache.cleanup();
  userCache.cleanup();
  configCache.cleanup();
}, 5 * 60 * 1000);
